import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "components/ui/Button";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable/DataTable";
import { Pill } from "components/ui/Pill/Pill";
import { useDeleteRelatioship } from "features/family/hooks/useDeleteFamilyRelation";

import { FAMILY_RELATION_LABEL } from "../PatientFamilySearch/constants";
import s from "./PatientFamilyTable.module.css";

const COLUMNS = [
  {
    id: "patientname",
    header: "Name",
    clickable: true,
  },
  {
    id: "dob",
    header: "DOB",
  },
  {
    id: "familyRelationType",
    header: "Relationship",
    render: (row) => {
      const label =
        FAMILY_RELATION_LABEL[row.familyRelationType] ??
        row.familyRelationType;
      if (!label) return "-";
      return (
        <Pill color="var(--green-900)" bg="#e7efeb" border="var(--green-900)">
          {label}
        </Pill>
      );
    },
  },
  {
    id: "canSeeRecords",
    header: "Can access",
    render: (row) =>
      row.canSeeRecords ? (
        <Pill color="var(--green-900)" bg="#e7efeb" border="var(--green-900)">
          Yes
        </Pill>
      ) : (
        <Pill color="var(--gray-600)" bg="var(--gray-100)" border="var(--gray-300)">
          No
        </Pill>
      ),
  },
];

const PatientsFamilyTable = ({
  relationshipListList,
  onAdd,
  onEdit,
  onPatientClick,
  refetchRelationships,
}) => {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { mutate: deleteRelationship, isPending } =
    useDeleteRelatioship(refetchRelationships);

  const columns = COLUMNS.map((col) =>
    col.id === "patientname"
      ? {
          ...col,
          onClick: (row) => onPatientClick?.(row.patientId, row.patientname),
        }
      : col
  );

  const rowActions = [
    { label: "Edit", icon: Pencil, primary: true, onClick: (row) => onEdit(row) },
    { label: "Delete", icon: Trash2, primary: true, onClick: (row) => setDeleteTarget(row) },
  ];

  return (
    <div className={s.topWrapper}>
      <div className={s.header}>
        <h3 className={s.title}>Patient family relationships</h3>
        <Button variant="text" onClick={onAdd} endIcon={Plus}>
          Add New
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={relationshipListList ?? []}
        rowActions={rowActions}
        emptyTitle="No family relationships"
        emptyText="Add a family member to get started."
        noScroll
      />
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() =>
          deleteRelationship(deleteTarget.id, {
            onSuccess: () => setDeleteTarget(null),
          })
        }
        isLoading={isPending}
        title="Delete relationship"
        message={`Are you sure you want to delete the relationship with ${
          deleteTarget?.patientname ?? "this patient"
        }?`}
        confirmLabel="Delete"
        buttonSize="small"
      />
    </div>
  );
};

export { PatientsFamilyTable };
